import { SVLoader } from '@/components/core';
import DefaultLayout from '@/components/layouts/DefaultLayout';
import ScheduledTransactionCancelButton from '@/components/scheduledTransactions/ScheduledTransactionCancelButton';
import ScheduledfTransactionDetailsCard from '@/components/scheduledTransactions/ScheduledTransactionDetailsCard';
import { ROUTES } from '@/constants/routes';
import { useGetBankAccountDetails } from '@/hooks/requests/useBankAccounts';
import { useGetScheduledTransactionDetails } from '@/hooks/requests/useTransactions';
import { useAuthUser } from '@/hooks/stores/useAuthUser';
import { ScheduledTransactionStatus } from '@/types/transactions';
import { generateBankAccountDisplayName } from '@/utils/bankAccounts';
import { Card, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';

export default function ScheduledTransactionsDetailsPage() {
  const { isStaff } = useAuthUser();
  const { bankAccountId = '', uuid = '' } = useParams();
  const { scheduledTransactionDetails, isScheduledTransactionDetailsPending } =
    useGetScheduledTransactionDetails(uuid);
  const { accountDetails, accountsDetailsIsPending } =
    useGetBankAccountDetails(bankAccountId);

  if (isScheduledTransactionDetailsPending || accountsDetailsIsPending) {
    return (
      <DefaultLayout header="Scheduled Transaction Details">
        <Card>
          <SVLoader />
        </Card>
      </DefaultLayout>
    );
  }

  if (!accountDetails || !scheduledTransactionDetails) {
    return (
      <DefaultLayout header="Scheduled Transaction Details">
        <Card>
          <Typography>
            Something went wrong retreiving the scheduled transaction details.
            Our team has been notified.
          </Typography>
        </Card>
      </DefaultLayout>
    );
  }

  const showCancelButton =
    isStaff &&
    scheduledTransactionDetails.status === ScheduledTransactionStatus.ACTIVE;
  const { applicationType, business, primaryOwner, owners } = accountDetails;
  const { fullName, email } = primaryOwner.profile;
  const { name: businessName } = business || {};
  const totalOwners = owners.length;
  const bankAccountName = generateBankAccountDisplayName({
    applicationType,
    businessName,
    fullName,
    email,
    totalOwners,
  });

  return (
    <DefaultLayout
      header={`Scheduled Transaction Details: ${scheduledTransactionDetails.description}`}
      breadcrumb={[
        { label: 'Bank Accounts', href: ROUTES.BANK_ACCOUNTS },
        {
          label: bankAccountName,
          href: `${ROUTES.BANK_ACCOUNTS}/${bankAccountId}`,
        },
        {
          label: 'Scheduled Transactions',
          href: `${ROUTES.BANK_ACCOUNTS}/${bankAccountId}${ROUTES.SCHEDULED_TRANSACTIONS}`,
        },
        { label: 'Scheduled Transaction Details', isActive: true },
      ]}
      buttons={
        showCancelButton
          ? [
              <ScheduledTransactionCancelButton
                key="cancel"
                scheduledTransactionDetails={scheduledTransactionDetails}
              />,
            ]
          : []
      }>
      <ScheduledfTransactionDetailsCard
        scheduledTransactionDetails={scheduledTransactionDetails}
      />
    </DefaultLayout>
  );
}
